import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, TextField } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useUpdateMediaMutation } from '@/state/mutations/media/use-update-media.ts';
import { updateMediaSchemaFactory } from '@/schemas/media/update-media.ts';

interface UpdateMediaFormProps {
  mediaId: string;
  title: string;
  onSuccess?: () => void;
}

export default function UpdateMediaForm({ mediaId, title, onSuccess }: UpdateMediaFormProps) {
  const notification = useNotifications();
  const t = useTranslations();
  const { mutate, data, isSuccess, isPending, error } = useUpdateMediaMutation();

  const updateMediaSchema = updateMediaSchemaFactory(t);
  type CreateMedia = z.infer<typeof updateMediaSchema>;
  const { control, handleSubmit } = useForm<CreateMedia>({
    resolver: zodResolver(updateMediaSchema),
    defaultValues: { title },
  });

  useEffect(() => {
    if (isSuccess && data) {
      onSuccess?.();
      notification.notifySuccess(t('media.notification.updateSuccess', { title: data.title }));
    }
  }, [isSuccess, data, t, notification, control, onSuccess]);

  useEffect(() => {
    if (error) {
      notification.notifyError(t('media.notification.updateError'));
    }
  }, [error, notification, t]);

  const onSubmit = (data: { title?: string }) => {
    mutate({ id: mediaId, title: data.title });
  };

  return (
    <Box
      noValidate
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        width: '100%',
        mb: '0.4rem',
        display: 'flex',
        alignItems: 'start',
      }}
    >
      <Controller
        name="title"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            autoFocus
            {...field}
            fullWidth
            type="text"
            variant="standard"
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
            onClick={(event) => event.stopPropagation()}
          />
        )}
      />
      <Button
        type="submit"
        variant="contained"
        size="small"
        loading={isPending}
        sx={{ ml: '1rem' }}
        onClick={(event) => event.stopPropagation()}
      >
        {t('common.update')}
      </Button>
    </Box>
  );
}
